/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEnvelope, 
  faPhone, 
  faMapMarkerAlt, 
  faClock,
  faComments,
  faHeadset,
  faRocket,
  faUsers,
  faBuilding,
  faGlobe,
  faHome
} from '@fortawesome/free-solid-svg-icons';
import { faLinkedin, faTwitter, faFacebook } from '@fortawesome/free-brands-svg-icons';
import '../styles/ContactUs.css';

const OFFICE_ADDRESS = 'Maxpro Infotech Ltd, B14 Parksuite Tower, Opp Parklands Police Station, Nairobi, Kenya';

const GOOGLE_MAPS_KEY =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_GOOGLE_MAPS_API_KEY) ||
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  '';

const ContactUs = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    category: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      alert('Thank you for contacting us! We will get back to you within 24 hours.');
      setFormData({
        name: '',
        email: '',
        subject: '',
        category: '',
        message: ''
      });
      setIsSubmitting(false);
    }, 1000);
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const contactInfo = [
    {
      icon: faEnvelope,
      title: 'Email Us',
      details: ['info@aksharjobs.com'],
      description: 'Send us an email and we\'ll respond within 4 hours'
    },
    {
      icon: faPhone,
      title: 'Call Us',
      details: ['+254 743 599050'],
      description: 'Speak with our support team directly'
    },
    {
      icon: faMapMarkerAlt,
      title: 'Visit Us',
      details: ['Maxpro Infotech Ltd', 'B14, Parksuite Tower', 'Opp Parklands Police Station', 'Nairobi, Kenya'],
      description: 'Our office in Nairobi'
    },
    {
      icon: faClock,
      title: 'Business Hours',
      details: ['Monday - Friday: 8:00 AM - 5:00 PM', 'Saturday: 8:00 AM - 1:00 PM'],
      description: 'East Africa Time (EAT)'
    }
  ];

  const departments = [
    {
      name: 'General Support',
      email: 'support@aksharjobs.com',
      description: 'Account issues, technical problems, general inquiries'
    },
    {
      name: 'Sales & Partnerships',
      email: 'sales@aksharjobs.com',
      description: 'Enterprise solutions, partnerships, bulk hiring'
    }
  ];

  return (
    <div className="contact_us_wrapper">
      {/* Back to Home Button */}
      <button
        type="button"
        onClick={() => navigate('/')}
        className="back-to-home-btn"
        style={{
          position: 'absolute',
          top: '20px',
          left: '20px',
          background: 'rgba(255, 255, 255, 0.25)',
          backdropFilter: 'blur(10px)',
          border: '2px solid rgba(255, 255, 255, 0.4)',
          color: '#ffffff',
          padding: '12px 24px',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '600',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          transition: 'all 0.3s ease',
          zIndex: 10,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          textShadow: '0 1px 2px rgba(0,0,0,0.3)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)';
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.25)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
        }}
      >
        <FontAwesomeIcon icon={faHome} />
        Back to Home
      </button>

      {/* Hero Section */}
      <section className="contact_hero">
        <div className="contact_hero_container">
          <div className="hero_content">
            <h1 className="hero_title">
              <FontAwesomeIcon icon={faHeadset} />
              Contact Us
            </h1>
            <p className="hero_subtitle">
              Have questions or need assistance? We're here to help! Reach out to our friendly support team 
              and we'll get back to you as soon as possible.
            </p>
            <div className="quick_stats">
              <div className="quick_stat">
                <FontAwesomeIcon icon={faClock} />
                <span>&lt; 4 Hour Response</span>
              </div>
              <div className="quick_stat">
                <FontAwesomeIcon icon={faUsers} />
                <span>24/7 Support</span>
              </div>
              <div className="quick_stat">
                <FontAwesomeIcon icon={faGlobe} />
                <span>Global Coverage</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="contact_methods_section">
        <div className="contact_container">
          <h2 className="section_title">Get In Touch</h2>
          <div className="contact_methods_grid">
            {contactInfo.map((info, index) => (
              <div key={index} className="contact_method_card">
                <div className="method_icon">
                  <FontAwesomeIcon icon={info.icon} />
                </div>
                <h3>{info.title}</h3>
                <div className="method_details">
                  {info.details.map((detail, detailIndex) => (
                    <span key={detailIndex}>{detail}</span>
                  ))}
                </div>
                <p className="method_description">{info.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Visit Our Office – Map */}
      <section className="contact_map_section">
        <div className="contact_container">
          <h2 className="section_title">Visit Our Office</h2>
          <p className="contact_map_subtitle">Find us in Nairobi, Kenya</p>
          <div className="contact_map_wrapper">
            {GOOGLE_MAPS_KEY ? (
              <iframe
                title="Office location – Maxpro Infotech Ltd, Nairobi"
                width="600"
                height="450"
                style={{ border: 0, width: '100%', maxWidth: '100%' }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_KEY}&q=${encodeURIComponent(OFFICE_ADDRESS)}&zoom=17`}
              />
            ) : (
              <div className="contact_map_fallback">
                <p>Map preview unavailable. Set <code>VITE_GOOGLE_MAPS_API_KEY</code> or <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> in your environment.</p>
              </div>
            )}
          </div>
          <div className="contact_map_address">
            <FontAwesomeIcon icon={faMapMarkerAlt} />
            <span>{OFFICE_ADDRESS}</span>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="contact_form_section">
        <div className="contact_container">
          <div className="contact_form_content">
            <div className="form_info">
              <h2>Send us a Message</h2>
              <p>Fill out the form below and our team will get back to you within 24 hours.</p>
              
              <div className="departments_list">
                <h3>Direct Contact</h3>
                {departments.map((dept, index) => (
                  <div key={index} className="department_item">
                    <strong>{dept.name}</strong>
                    <a href={`mailto:${dept.email}`}>{dept.email}</a>
                    <p>{dept.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <form className="contact_form" onSubmit={handleSubmit}>
              <div className="form_row">
                <div className="form_field">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter your full name"
                  />
                </div>
                
                <div className="form_field">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Enter your email address"
                  />
                </div>
              </div>
              
              <div className="form_row">
                <div className="form_field">
                  <label>Subject *</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder="What is this regarding?"
                  />
                </div>
                
                <div className="form_field">
                  <label>Category</label>
                  <select name="category" value={formData.category} onChange={handleChange}>
                    <option value="">Select a category</option>
                    <option value="general">General Inquiry</option>
                    <option value="technical">Technical Support</option>
                    <option value="account">Account Issues</option>
                    <option value="billing">Billing & Payments</option>
                    <option value="feature">Feature Request</option>
                    <option value="partnership">Partnership</option>
                  </select>
                </div>
              </div>
              
              <div className="form_field">
                <label>Message *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  placeholder="Please describe your question or issue in detail..."
                  rows="6"
                />
              </div>
              
              <button type="submit" className="submit_btn" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <FontAwesomeIcon icon={faRocket} className="spinning" />
                    Sending...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faEnvelope} />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;
